/*******************************************************************************
  Users.js

  Author: Advait

*******************************************************************************/

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { API } from "aws-amplify";
import { getTenants } from "../apihelpers/graphqlhelpers";
import { setTenants } from "../store/tenantSlice";
import AddUserModal from "../components/AddUserModal";
import ConfirmModal from "../components/ConfirmModal";
import { showNotification } from "../components/helper/notification";
import "../index.css";

function Users() {
  const [userData, setUserData] = useState([]);
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const dispatch = useDispatch();
  const tenantList = useSelector((state) => state.tenantList.tenants);

  const handleModalClose = (result) => {
    setIsModalOpen(false);

    if (result.status === "success") {
      showNotification(true, `User added successfully`);
      fetchUserData();
    } else if (result.status === "error") {
      showNotification(false, `${result.message}`);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await API.get("fataskman", `/users`);
      // Extract the Users array from the response
      const processedData = response.Users || [];
      setUserData(processedData);
    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      setUserDataLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener("click", closeMenu);
      return () => document.removeEventListener("click", closeMenu);
    }
  }, [contextMenu]);

  useEffect(() => {
    const fetchTenantsData = async () => {
      try {
        // Fetch tenant data when Users page loads
        const tenants = await getTenants(true);
        dispatch(setTenants(tenants));
      } catch (err) {
        console.error("Error fetching tenant data:", err);
      }
    };

    fetchTenantsData();
  }, [dispatch]);

  // Get attribute for a user
  const getAttributeValue = useCallback((user, attributeName) => {
    const attribute = user.Attributes?.find((attr) => attr.Name === attributeName);
    return attribute ? attribute.Value : "";
  }, []);

  // get tenant name based on tid
  const getTenantCompanyName = useCallback(
    (tid) => {
      if (!tid) return "";
      const tenant = tenantList.find((t) => t.id === tid);
      return tenant ? tenant.company : "";
    },
    [tenantList]
  );

  // Sanitize date, return only date
  const sanitizeDate = useCallback((dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  }, []);

  // Sanitize confirmation status text
  // replace _ with spaces and capitalize first letter of each word
  const sanitizeConfirmationStatus = useCallback((status) => {
    if (!status) return "";
    return status
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }, []);

  const handleSort = useCallback(
    (column) => {
      if (sortBy === column) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortBy(column);
        setSortDirection("asc");
      }
    },
    [sortBy, sortDirection]
  );

  // Function to get sort value for a user
  const getSortValue = useCallback(
    (user, column) => {
      let value;
      switch (column) {
        case "username":
          value = user.Username || "";
          break;
        case "email":
          value = getAttributeValue(user, "email");
          break;
        case "tenant":
          value = user.isSysadmin
            ? "Sysadmin"
            : getTenantCompanyName(getAttributeValue(user, "custom:tid"));
          break;
        case "status":
          value = user.Enabled ? "Enabled" : "Disabled";
          break;
        case "confirmationStatus":
          value = user.UserStatus || "";
          break;
        case "lastAccessed":
          return new Date(user.UserLastAccessedDate || 0);
        case "lastUpdated":
          return new Date(user.UserLastModifiedDate || 0);
        case "created":
          return new Date(user.UserCreateDate || 0);
        default:
          value = "";
      }
      return typeof value === "string" ? value.toLowerCase() : value;
    },
    [getAttributeValue, getTenantCompanyName]
  );

  const sortedData = useMemo(() => {
    if (!sortBy) return userData;

    return [...userData].sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [userData, sortBy, sortDirection, getSortValue]);

  const getSortingIcon = useCallback(
    (column) => {
      if (sortBy !== column) return " ↕️";
      return sortDirection === "asc" ? " ↑" : " ↓";
    },
    [sortBy, sortDirection]
  );

  const handleContextMenu = (e, username) => {
    e.preventDefault();

    // Calculate menu position with bounds checking
    const menuHeight = 100; // Approx height of the context menu
    const menuWidth = 150; // Approx width of the context menu

    let x = e.pageX;
    let y = e.pageY;

    // Check if menu would go off the bottom of the viewport
    if (e.clientY + menuHeight > window.innerHeight) {
      y = e.pageY - menuHeight;
    }

    // Check if menu would go off the right of the viewport
    if (e.clientX + menuWidth > window.innerWidth) {
      x = e.pageX - menuWidth;
    }

    setContextMenu({ x, y, username });
  };

  const createOrResendUser = async (email, role, tenantId, isResend = false) => {
    const encodedEmail = encodeURIComponent(email);

    if (isResend) {
      return await API.post("fataskman", `/users/${encodedEmail}/resend`, {});
    }

    return await API.put("fataskman", `/users/${encodedEmail}`, {
      body: { role, tenantId },
    });
  };

  const handleMenuAction = (action) => {
    setConfirmModal({
      username: contextMenu.username,
      action: action,
    });
    setContextMenu(null);
  };

  const handleConfirm = async () => {
    try {
      if (confirmModal.action === "Resend Invite") {
        await createOrResendUser(confirmModal.username, null, null, true);
        showNotification(true, "Invite resent successfully");
      } else if (confirmModal.action === "Delete User") {
        const encodedEmail = encodeURIComponent(confirmModal.username);
        await API.del("fataskman", `/users/${encodedEmail}`, {});
        showNotification(true, "User deleted successfully");
        fetchUserData();
      }
    } catch (err) {
      const action = confirmModal.action === "Delete User" ? "delete user" : "resend invite";
      showNotification(false, err.response?.data?.message || `Failed to ${action}`);
    }
    setConfirmModal(null);
  };

  return (
    <div className="fullPageDiv">
      <div className="users-header-container">
        <h3>Users ({userData.length})</h3>
        <button className="saveButton" onClick={() => setIsModalOpen(true)}>
          Add User
        </button>
      </div>

      <AddUserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={createOrResendUser}
      />

      {userDataLoading ? (
        <div>Loading users...</div>
      ) : (
        <div className="users-container">
          <table className="users-table">
            <thead>
              <tr className="users-table-header">
                <th className="users-table-header-cell" onClick={() => handleSort("username")}>
                  Username{getSortingIcon("username")}
                </th>
                <th className="users-table-header-cell" onClick={() => handleSort("email")}>
                  Email Address{getSortingIcon("email")}
                </th>
                <th className="users-table-header-cell" onClick={() => handleSort("tenant")}>
                  Tenant{getSortingIcon("tenant")}
                </th>
                <th className="users-table-header-cell" onClick={() => handleSort("status")}>
                  Status{getSortingIcon("status")}
                </th>
                <th
                  className="users-table-header-cell"
                  onClick={() => handleSort("confirmationStatus")}
                >
                  <div className="users-header-with-icon">
                    <div className="users-header-text">
                      <span>Confirmation</span>
                      <span>Status</span>
                    </div>
                    <span className="users-sort-icon">
                      {getSortingIcon("confirmationStatus")}
                    </span>
                  </div>
                </th>
                <th
                  className="users-table-header-cell"
                  onClick={() => handleSort("lastAccessed")}
                >
                  <div className="users-header-with-icon">
                    <div className="users-header-text">
                      <span>Last Accessed</span>
                      <span>Date</span>
                    </div>
                    <span className="users-sort-icon">{getSortingIcon("lastAccessed")}</span>
                  </div>
                </th>
                <th
                  className="users-table-header-cell"
                  onClick={() => handleSort("lastUpdated")}
                >
                  <div className="users-header-with-icon">
                    <div className="users-header-text">
                      <span>Last Updated</span>
                      <span>Date</span>
                    </div>
                    <span className="users-sort-icon">{getSortingIcon("lastUpdated")}</span>
                  </div>
                </th>
                <th className="users-table-header-cell" onClick={() => handleSort("created")}>
                  <div className="users-header-with-icon">
                    <div className="users-header-text">
                      <span>Created</span>
                      <span>Date</span>
                    </div>
                    <span className="users-sort-icon">{getSortingIcon("created")}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((user, index) => (
                <tr key={index}>
                  <td
                    className="users-table-cell"
                    onContextMenu={(e) => handleContextMenu(e, user.Username)}
                  >
                    {user.Username}
                  </td>
                  <td className="users-table-cell">{getAttributeValue(user, "email")}</td>
                  <td className="users-table-cell">
                    {user.isSysadmin
                      ? "Sysadmin"
                      : getTenantCompanyName(getAttributeValue(user, "custom:tid"))}
                  </td>
                  <td className="users-table-cell">{user.Enabled ? "Enabled" : "Disabled"}</td>
                  <td className="users-table-cell">
                    {sanitizeConfirmationStatus(user.UserStatus)}
                  </td>
                  <td className="users-table-cell">
                    {sanitizeDate(user.UserLastAccessedDate)}
                  </td>
                  <td className="users-table-cell">
                    {sanitizeDate(user.UserLastModifiedDate)}
                  </td>
                  <td className="users-table-cell">{sanitizeDate(user.UserCreateDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {contextMenu && (
        <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
          <div onClick={() => handleMenuAction("Resend Invite")}>Resend Invite</div>
          <hr style={{ margin: "0 10px" }} />
          <div onClick={() => handleMenuAction("Delete User")}>Delete User</div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirm}
        title={
          confirmModal?.action === "Delete User"
            ? "Confirm Delete User"
            : "Confirm Resend Invite"
        }
        message={
          confirmModal?.action === "Delete User"
            ? `Are you sure you want to delete user ${confirmModal?.username}?`
            : `Are you sure you want to resend invite for ${confirmModal?.username}?`
        }
      />
    </div>
  );
}
export default Users;
